/**
 * Detects which language the host page is presenting, and follows changes.
 *
 * Mirrors host-theme.ts: read the cheapest reliable signal first, fall back
 * progressively, and watch for changes so a language switcher that never
 * reloads the page still updates the widget.
 */

/** Languages the widget UI is translated into. */
export const SUPPORTED_LANGUAGES = [
  "en-GB",
  "en-US",
  "en-AU",
  "de",
  "ar",
  "zh",
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

/** Languages written right-to-left. */
const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur"]);

export function isRtl(language: string): boolean {
  return RTL_LANGUAGES.has(baseLanguage(language));
}

/** "en-AU" -> "en". */
function baseLanguage(tag: string): string {
  return tag.trim().toLowerCase().split(/[-_]/)[0];
}

/**
 * Maps any BCP-47 tag onto a language we support.
 *
 * Regional English falls back to the closest variant rather than a generic
 * one, so an en-NZ visitor gets en-AU spelling rather than American.
 */
export function normalizeLanguage(
  tag: string | null | undefined,
  fallback: LanguageCode = "en-GB",
): LanguageCode {
  if (!tag) return fallback;
  const lower = tag.trim().toLowerCase();

  const exact = SUPPORTED_LANGUAGES.find((l) => l.toLowerCase() === lower);
  if (exact) return exact;

  const base = baseLanguage(lower);
  const region = lower.split(/[-_]/)[1];

  if (base === "en") {
    if (region === "us" || region === "ca") return "en-US";
    if (region === "au" || region === "nz") return "en-AU";
    if (region) return "en-GB";
    // Bare "en" keeps whatever the business configured, when that is English.
    return fallback.startsWith("en") ? fallback : "en-GB";
  }
  if (base === "de") return "de";
  if (base === "ar") return "ar";
  // Simplified and traditional both map to one entry: the strings below are
  // Simplified, which is what zh-CN, zh-SG and a bare "zh" expect.
  if (base === "zh") return "zh";

  return fallback;
}

/** Reads the language from the URL path or query, e.g. /de/ or ?lang=ar. */
function fromUrl(): string | null {
  const { pathname, search } = window.location;

  const query = new URLSearchParams(search);
  const param =
    query.get("lang") ?? query.get("locale") ?? query.get("language");
  if (param) return param;

  // A leading path segment that looks like a language tag: /de/, /en-au/.
  const segment = pathname.split("/").filter(Boolean)[0];
  if (segment && /^[a-z]{2}(([-_])[a-z]{2})?$/i.test(segment)) return segment;

  return null;
}

/**
 * Resolves the page's language. `configured` is the business's own default,
 * used when the page says nothing.
 */
/**
 * Narrows a detected language to what the business actually offers. A page
 * language outside the list falls back to another region of the same language
 * where possible, then to the default.
 */
export function restrictToEnabled(
  detected: LanguageCode,
  enabled: string[],
  fallback: LanguageCode,
): LanguageCode {
  if (enabled.length === 0) return fallback;
  if (enabled.includes(detected)) return detected;

  const base = detected.split("-")[0];
  const sameBase = enabled.find((l) => l.split("-")[0] === base);
  if (sameBase) return normalizeLanguage(sameBase, fallback);

  return fallback;
}

export function detectLanguage(configured: LanguageCode = "en-GB"): LanguageCode {
  // 1. What the page declares. Maintained by every serious multilingual site,
  //    since screen readers and search engines depend on it.
  const declared = document.documentElement.getAttribute("lang");
  if (declared) return normalizeLanguage(declared, configured);

  // 2. The URL. Catches sites that translate but forget to update lang.
  const fromPath = fromUrl();
  if (fromPath) return normalizeLanguage(fromPath, configured);

  // 3. The visitor's own browser preference.
  const preferred =
    typeof navigator !== "undefined" ? navigator.language : null;
  if (preferred) return normalizeLanguage(preferred, configured);

  return configured;
}

/**
 * Calls back when the page's language changes a switcher that swaps
 * `lang` in place, or a site that dispatches its own event. Returns a
 * function that stops watching.
 */
export function watchLanguage(
  configured: LanguageCode,
  onChange: (language: LanguageCode) => void,
): () => void {
  let current = detectLanguage(configured);

  const check = () => {
    const next = detectLanguage(configured);
    if (next !== current) {
      current = next;
      onChange(next);
    }
  };

  // Client-side switchers (next-intl, vue-i18n, Weglot) mutate lang in place.
  const observer = new MutationObserver(check);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang"],
  });

  // Escape hatch for switchers that change neither lang nor the URL:
  //   window.dispatchEvent(new CustomEvent("minaya:language", {
  //     detail: { language: "ar" },
  //   }));
  const onManual = (event: Event) => {
    const detail = (event as CustomEvent<{ language?: string }>).detail;
    if (detail?.language) {
      const next = normalizeLanguage(detail.language, configured);
      if (next !== current) {
        current = next;
        onChange(next);
      }
      return;
    }
    check();
  };
  window.addEventListener("minaya:language", onManual);

  // A single-page app can change the URL without touching lang.
  window.addEventListener("popstate", check);

  return () => {
    observer.disconnect();
    window.removeEventListener("minaya:language", onManual);
    window.removeEventListener("popstate", check);
  };
}
